import { useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { Icon, type IconName } from "@frontend/components/atoms/Icon";
import { useIslandHost } from "@frontend/contexts";

import { Button } from "./Button";
import { type ButtonDataProps, buttonDataSchema } from "./Button.schema";
import { BUTTON_TAG_NAME } from "./constants";

export interface ButtonHandle {
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [BUTTON_TAG_NAME]: { props: ButtonDataProps; handle: ButtonHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-button": HTMLElement & ButtonHandle;
  }
}

export function RWCButton({
  ref,
  disabled: initialDisabled,
  icon,
  size,
  type,
  href,
  target,
  ariaLabel,
  iconOnly,
  label,
  ...props
}: ButtonDataProps & { ref?: Ref<ButtonHandle> }) {
  const host = useIslandHost();

  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  const handle: ButtonHandle = {
    disabled: (value = true) => setDisabled(value),
  };

  useImperativeHandle(ref, () => handle, []);

  function handleClick() {
    const form = getElementInternals(host)?.form;

    if (type === "submit") {
      form?.requestSubmit();
    } else if (type === "reset") {
      form?.reset();
    }
  }

  const behaviour = href
    ? {
        render: (
          <a
            href={href}
            target={target}
            rel={target === "_blank" ? "noreferrer" : undefined}
          />
        ),
      }
    : { type, onClick: handleClick };

  if (!icon) {
    return (
      <Button
        {...props}
        {...behaviour}
        label={label}
        aria-label={ariaLabel}
        size={size}
        disabled={disabled}
      />
    );
  }

  return (
    <Button
      {...props}
      {...behaviour}
      label={iconOnly ? undefined : label}
      aria-label={ariaLabel ?? (iconOnly ? label : undefined)}
      size={size}
      leftSection={<Icon name={icon as IconName} size={size ?? "sm"} />}
      disabled={disabled}
    />
  );
}

componentManager.register(
  rwc(BUTTON_TAG_NAME, {
    component: RWCButton,
    dataSchema: buttonDataSchema,
    formAssociated: true,
  }),
);
